
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock,
  MessageCircle,
  Mail,
  MapPin,
  ImageIcon,
  UtensilsCrossed,
  ShoppingBag,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const DEFAULT_HERO = "https://images.unsplash.com/photo-1673907161385-a21e1b10d68c";

export const WHATSAPP_SERVICES = [
  "Palais Maasaï",
  "Le Comptoir Maasaï",
  "Maasaï Services",
];

export const GALLERY_CATEGORIES = [
  { id: "plats", label: "Plats" },
  { id: "boissons", label: "Boissons" },
  { id: "interieur", label: "Intérieur" },
  { id: "soirees", label: "Soirées" },
  { id: "clients", label: "Clients" },
];

export const useSettings = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({}));
  }, []);
  return settings;
};

export const siteText = (settings, key) => settings?.texts?.[key] || "";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const Marquee = ({ items }) => {
  const content = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-white/10 py-6" data-testid="editorial-marquee">
      <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
        {content.map((item, i) => (
          <span
            key={i}
            className="flex items-center gap-10 text-sm font-semibold uppercase tracking-[0.35em] text-ivory/40"
          >
            {item}
            <span className="h-1.5 w-1.5 rounded-full bg-copper" />
          </span>
        ))}
      </div>
    </div>
  );
};

export const PhotoSlot = ({
  src,
  alt = "",
  ratio = "aspect-[4/5]",
  label = "Photo à venir",
  className = "",
  testId,
}) => (
  <div
    data-testid={testId}
    className={`relative overflow-hidden rounded-lg border border-white/10 bg-coal ${ratio} ${className}`}
  >
    {src ? (
      <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />
    ) : (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[radial-gradient(circle_at_50%_40%,rgba(198,168,124,0.08),transparent_70%)]">
        <ImageIcon className="h-6 w-6 text-gold/40" strokeWidth={1.25} />
        <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-ivory/30">
          {label}
        </span>
      </div>
    )}
  </div>
);

export const PageHero = ({ overline, title, children, testId, image = "", ctas = [] }) => (
  <section className="relative overflow-hidden px-6 pb-16 pt-36 md:px-12 md:pb-20 md:pt-44 lg:pt-56" data-testid={testId}>
    {image && (
      <>
        <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid={`${testId}-image`} />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink to-transparent" />
      </>
    )}
    <div className="relative mx-auto max-w-6xl">
      <Reveal>
        <p className="overline mb-6">{overline}</p>
        <h1 className="max-w-3xl text-4xl font-bold tracking-tight text-ivory sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {children && (
          <div className="mt-8 max-w-xl text-base leading-relaxed text-ivory/60 md:text-lg">
            {children}
          </div>
        )}
        {ctas.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-4" data-testid={`${testId}-ctas`}>
            {ctas.map((cta, i) =>
              cta.href ? (
                <a
                  key={i}
                  href={cta.href}
                  className={i === 0 ? "btn-primary" : "btn-outline"}
                  data-testid={`${testId}-cta-${i}`}
                >
                  {cta.label}
                </a>
              ) : (
                <button
                  key={i}
                  type="button"
                  onClick={cta.onClick}
                  className={i === 0 ? "btn-primary" : "btn-outline"}
                  data-testid={`${testId}-cta-${i}`}
                >
                  {cta.label}
                </button>
              )
            )}
          </div>
        )}
      </Reveal>
    </div>
  </section>
);

export const EmptyState = ({ text = "Contenu à venir." }) => (
  <div
    className="rounded-lg border border-dashed border-white/15 px-8 py-20 text-center"
    data-testid="empty-state"
  >
    <p className="text-sm uppercase tracking-[0.25em] text-ivory/35">{text}</p>
  </div>
);

export const DEFAULT_CONTACT = {
  phone_display: "07 56 89 53 54",
  whatsapp: "33756895354",
  email: "lepmaasai@gmail.com",
  address_name: "Palais Maasaï",
  address_street: "87 rue Berbisey",
  address_city: "21000 Dijon",
};

export const DEFAULT_HOURS = {
  week_label: "Mardi à Vendredi",
  week_1: "11h45 – 15h00",
  week_2: "17h00 – 02h00",
  weekend_label: "Samedi, Dimanche et Lundi",
  weekend_1: "17h00 – 02h00",
};

export const UNIVERSE_SPACES = [
  {
    id: "palais",
    name: "Palais Maasaï",
    tagline: "Restaurant · Bar Lounge · Événements · À emporter",
    description:
      "Cuisine fusion d'inspiration africaine, bar lounge et événements à Dijon. Réservez votre table ou commandez à emporter.",
    path: "/palais",
    icon: UtensilsCrossed,
  },
  {
    id: "comptoir",
    name: "Le Comptoir Maasaï",
    tagline: "Boutique en ligne · Jus de bissap · Produits africains",
    description:
      "Épicerie africaine, boissons, bissap et produits maison en Click & Collect. Découvrez nos nouveautés et meilleures ventes.",
    path: "/comptoir",
    icon: ShoppingBag,
  },
  {
    id: "services",
    name: "Maasaï Services",
    tagline: "Ménage · Nettoyage · Entretien · Devis",
    description:
      "Ménage à domicile, bureaux, commerces, locations saisonnières et après travaux. Devis gratuit et réservation en ligne.",
    path: "/services",
    icon: Sparkles,
  },
];

export const useUniverseSpaces = () => {
  const settings = useSettings();
  return UNIVERSE_SPACES.map((s) => ({
    ...s,
    description: settings?.texts?.[`universe_desc_${s.id}`] || s.description,
  }));
};

export const UniverseSection = ({ current }) => {
  const settings = useSettings();
  const spaces = useUniverseSpaces();
  const photos = settings?.universe_photos || {};
  return (
  <section className="border-t border-white/5 px-6 py-24 md:px-12 md:py-28" data-testid="universe-section">
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <p className="overline mb-6">Découvrez l'Univers Maasaï</p>
        <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
          Nos autres activités
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {spaces.filter((space) => space.id !== current).map((space, i) => (
          <Reveal key={space.id} delay={i * 0.1}>
            <div
              className="group h-full overflow-hidden rounded-lg border border-white/10 bg-coal/60 transition-colors hover:border-gold/40"
              data-el="univers.card"
              data-testid={`universe-link-${space.id}`}
            >
              <PhotoSlot src={photos[space.id]} alt={space.name} ratio="aspect-[16/7]" />
              <div className="p-8">
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gold/30 text-gold">
                    <space.icon className="h-5 w-5" strokeWidth={1.25} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold tracking-tight text-ivory transition-colors group-hover:text-gold">
                      {space.name}
                    </h3>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-ivory/40">
                      {space.tagline}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-ivory/55">{space.description}</p>
                <Link
                  to={space.path}
                  className="btn-outline mt-6 !h-10 !px-5 !text-[11px]"
                  data-testid={`universe-discover-${space.id}`}
                >
                  Découvrir
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export const InfoCards = () => {
  const settings = useSettings();
  const contact = { ...DEFAULT_CONTACT, ...(settings?.contact || {}) };
  const hours = { ...DEFAULT_HOURS, ...(settings?.hours || {}) };

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="info-cards">
      <div className="rounded-lg border border-white/10 bg-coal/60 p-6 transition-colors hover:border-gold/30">
        <Clock className="mb-5 h-5 w-5 text-copper" strokeWidth={1.5} />
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">Horaires</h3>
        <p className="text-xs font-semibold uppercase tracking-wider text-gold">{hours.week_label}</p>
        <p className="mt-1.5 text-sm text-ivory/60">{hours.week_1}</p>
        {hours.week_2 && <p className="text-sm text-ivory/60">{hours.week_2}</p>}
        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-gold">
          {hours.weekend_label}
        </p>
        <p className="mt-1.5 text-sm text-ivory/60">{hours.weekend_1}</p>
      </div>

      <div className="rounded-lg border border-white/10 bg-coal/60 p-6 transition-colors hover:border-gold/30">
        <MessageCircle className="mb-5 h-5 w-5 text-copper" strokeWidth={1.5} />
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">WhatsApp</h3>
        <a
          href={`https://wa.me/${contact.whatsapp}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-ivory/60 transition-colors hover:text-gold"
          data-testid="info-whatsapp"
        >
          {contact.phone_display}
        </a>
      </div>

      <div className="rounded-lg border border-white/10 bg-coal/60 p-6 transition-colors hover:border-gold/30">
        <Mail className="mb-5 h-5 w-5 text-copper" strokeWidth={1.5} />
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">Email</h3>
        <a
          href={`mailto:${contact.email}`}
          className="break-all text-sm text-ivory/60 transition-colors hover:text-gold"
          data-testid="info-email"
        >
          {contact.email}
        </a>
      </div>

      <div className="rounded-lg border border-white/10 bg-coal/60 p-6 transition-colors hover:border-gold/30">
        <MapPin className="mb-5 h-5 w-5 text-copper" strokeWidth={1.5} />
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">Adresse</h3>
        <p className="text-sm leading-relaxed text-ivory/60" data-testid="info-address">
          {contact.address_name}
          <br />
          {contact.address_street}
          <br />
          {contact.address_city}
        </p>
      </div>
    </div>
  );
};

export const FaqSection = ({ scope, title = "Questions fréquentes" }) => {  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api
      .get(`/faqs?scope=${scope}`)
      .then((r) => setFaqs(r.data))
      .catch(() => {});
  }, [scope]);
  if (faqs.length === 0) return null;
  return (
    <section className="border-t border-white/5 px-6 py-24 md:px-12 md:py-28" data-testid={`faq-section-${scope}`}>
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <p className="overline mb-6">FAQ</p>
          <h2 className="text-2xl font-bold tracking-tight text-ivory sm:text-3xl">{title}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-10" data-testid={`faq-accordion-${scope}`}>
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-white/10" data-testid={`faq-item-${f.id}`}>
                <AccordionTrigger className="text-base font-semibold text-ivory hover:text-gold hover:no-underline">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-ivory/55">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

// Carrousel automatique + manuel pour les fiches multi-photos.
// Vitesse réglable via Réglages → Design → carousel_speed (secondes, défaut 4).
export const ItemCarousel = ({ photos = [], alt = "", ratio = "aspect-square", testId }) => {
  const settings = useSettings();
  const [index, setIndex] = useState(0);
  const speed = Math.max(2, parseInt(settings?.design?.carousel_speed, 10) || 4);
  const valid = (photos || []).filter(Boolean);

  useEffect(() => {
    if (valid.length <= 1) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % valid.length), speed * 1000);
    return () => clearInterval(timer);
  }, [valid.length, speed]);

  if (valid.length === 0) {
    return <PhotoSlot src="" alt={alt} ratio={ratio} />;
  }
  if (valid.length === 1) {
    return <PhotoSlot src={valid[0]} alt={alt} ratio={ratio} />;
  }

  return (
    <div className={`group/car relative ${ratio} overflow-hidden rounded-md border border-white/10 bg-white/5`} data-testid={testId}>
      {valid.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`${alt} ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndex((index - 1 + valid.length) % valid.length); }}
        aria-label="Photo précédente"
        className="absolute left-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-ivory opacity-0 transition-opacity group-hover/car:opacity-100"
        data-testid={testId ? `${testId}-prev` : undefined}
      >
        ‹
      </button>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndex((index + 1) % valid.length); }}
        aria-label="Photo suivante"
        className="absolute right-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-ivory opacity-0 transition-opacity group-hover/car:opacity-100"
        data-testid={testId ? `${testId}-next` : undefined}
      >
        ›
      </button>
      <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
        {valid.map((_, i) => (
          <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === index ? "bg-gold" : "bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
};

